import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../App";
import Avatar from "../components/Avatar";
import { API, typeLabel } from "../api";

const TYPE_ICONS = { "CP": "🏖️", "RTT": "🕐", "Maladie": "🤒", "Maternite": "👶", "Paternite": "👨‍👶", "Pacs": "💍" };
const typeIcon = (t) => TYPE_ICONS[t] || "📅";

function Validations() {
  const { token, user } = useAuth();
  const [leaves, setLeaves] = useState([]);
  const [notification, setNotification] = useState("");
  const [notifType, setNotifType] = useState("success");
  const [processing, setProcessing] = useState({});
  const [search, setSearch] = useState("");

  const loadLeaves = async () => {
    const r = await axios.get(API + "/leaves/team", {
      headers: { Authorization: "Bearer " + token }
    });
    setLeaves(r.data);
    return r.data;
  };

  useEffect(() => { loadLeaves(); }, [token]);

  const showNotif = (msg, type) => {
    setNotification(msg);
    setNotifType(type);
    setTimeout(() => setNotification(""), 5000);
  };

  const handleConfirmCancel = async (id, confirm, nom, prenom) => {
    try {
      await axios.put(API + "/leaves/" + id + "/confirm-cancel",
        { confirm },
        { headers: { Authorization: "Bearer " + token } }
      );
      await loadLeaves();
      showNotif(
        confirm ? `✅ Annulation de ${prenom} ${nom} confirmée.` : `❌ Annulation de ${prenom} ${nom} refusée.`,
        confirm ? "success" : "refused"
      );
    } catch (err) {
      showNotif("Erreur lors du traitement.", "error");
    }
  };

  const handleStatut = async (id, statut, nom, prenom) => {
    setProcessing(p => ({ ...p, [id]: statut }));
    window.dispatchEvent(new CustomEvent('validationUpdated', { detail: { decrement: true } }));
    try {
      await axios.put(API + "/leaves/" + id + "/statut",
        { statut },
        { headers: { Authorization: "Bearer " + token } }
      );
      const updatedLeaves = await loadLeaves();
      setProcessing(p => ({ ...p, [id]: null }));
      if (statut === "approved") {
        // Vérifier si la demande est vraiment approuvée ou encore en attente d'un autre associé
        const updatedLeave = (updatedLeaves || []).find(l => l.id === id);
        const stillPending = updatedLeave && updatedLeave.statut === "pending";
        if (stillPending) {
          const pendingApprovals = (updatedLeave.approvals || []).filter(a => a.statut === "pending");
          showNotif(`✅ Votre validation est enregistrée. En attente de ${pendingApprovals.length > 0 ? "l'autre responsable" : "validation complémentaire"}.`, "success");
        } else {
          showNotif(`✅ Demande de ${prenom} ${nom} approuvée !`, "success");
        }
      } else {
        showNotif(`❌ Demande de ${prenom} ${nom} refusée.`, "refused");
      }
    } catch (err) {
      setProcessing(p => ({ ...p, [id]: null }));
      window.dispatchEvent(new CustomEvent('validationUpdated', { detail: { decrement: false } }));
      showNotif("Erreur lors du traitement.", "error");
    }
  };

  // Concurrent absences: combien d'autres collègues sont absents en même temps
  const getConcurrent = (leave) => {
    return leaves.filter(l =>
      l.id !== leave.id &&
      l.statut === "approved" &&
      l.date_debut.substring(0, 10) <= leave.date_fin.substring(0, 10) &&
      l.date_fin.substring(0, 10) >= leave.date_debut.substring(0, 10)
    );
  };

  const filterLeaves = (list) => search
    ? list.filter(l => (l.prenom + " " + l.nom).toLowerCase().includes(search.toLowerCase()))
    : list;

  // La DAF ne valide pas les congés des collabs (rôle des associés),
  // mais elle valide ceux des assistantes (rattachées au DAF) :
  // on affiche les demandes en attente dont elle est responsable dans leave_approvals.
  const isDaf = user?.role === "daf";
  // Vrai si l'utilisateur courant a déjà statué (approuvé/refusé) sur cette demande
  const jAiStatue = (l) => Array.isArray(l.approvals) && l.approvals.some(a => a.associe_id === user.id && a.statut !== "pending");
  const pending = filterLeaves(leaves.filter(l => {
    if (l.statut !== "pending") return false;
    if (isDaf) {
      return Array.isArray(l.approvals) && l.approvals.some(a => a.associe_id === user.id && a.statut === "pending");
    }
    // Associé : ne plus afficher en attente si j'ai déjà validé de mon côté
    return !jAiStatue(l);
  }));
  // Demandes que j'ai validées mais encore en attente d'un autre responsable
  const waitingOthers = filterLeaves(leaves.filter(l => l.statut === "pending" && !isDaf && jAiStatue(l)));
  const cancelRequests = filterLeaves(leaves.filter(l => l.statut === "approved" && l.cancel_requested));
  const processed = filterLeaves(leaves.filter(l => l.statut !== "pending" && !l.cancel_requested));

  const statusCfg = {
    approved: { bg: "#EAF3DE", color: "#3B6D11", label: "Approuvé", icon: "✅" },
    refused: { bg: "#FCEBEB", color: "#A32D2D", label: "Refusé", icon: "❌" },
    pending: { bg: "#FAEEDA", color: "#854F0B", label: "En attente", icon: "⏳" },
  };

  const LeaveCard = ({ l, showActions, isCancelRequest }) => {
    const isProc = processing[l.id];
    const concurrent = getConcurrent(l);
    const debut = new Date(l.date_debut).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
    const fin = new Date(l.date_fin).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    const cfg = statusCfg[l.statut] || {};

    return (
      <div style={{
        background: "#fff", borderRadius: 12, padding: "16px 20px",
        border: `1px solid ${isCancelRequest ? "#F5C97A" : showActions ? "#e0e0e0" : "#eee"}`,
        boxShadow: showActions ? "0 2px 8px rgba(0,0,0,0.06)" : "0 1px 3px rgba(0,0,0,0.04)",
        opacity: isProc ? 0.5 : 1, transition: "opacity 0.3s",
        marginBottom: 10,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
          {/* Avatar + nom */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 160 }}>
            <Avatar prenom={l.prenom} nom={l.nom} size={38} fontSize={13} />
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: "#222" }}>{l.prenom} {l.nom}</div>
              <div style={{ fontSize: 11, color: "#999", marginTop: 1 }}>
                {new Date(l.created_at).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" })}
              </div>
            </div>
          </div>

          {/* Détail du congé */}
          <div style={{ flex: 1, minWidth: 180 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
              <span style={{ fontSize: 18 }}>{typeIcon(l.type)}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#333" }}>{typeLabel(l.type)}</span>
              <span style={{ background: "#EEEDFE", color: "#7C6FE0", padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                {parseFloat(l.jours)} jour{parseFloat(l.jours) > 1 ? "s" : ""}
              </span>
              {!showActions && (
                <span style={{ background: cfg.bg, color: cfg.color, padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
                  {cfg.icon} {cfg.label}
                </span>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#777" }}>📅 Du {debut} au {fin}</div>
            {l.commentaire && (
              <div style={{ fontSize: 11, color: "#aaa", marginTop: 4, fontStyle: "italic" }}>"{l.commentaire}"</div>
            )}

            {/* Absences simultanées */}
            {showActions && concurrent.length > 0 && (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 10, color: "#854F0B", background: "#FAEEDA", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>
                  ⚠️ {concurrent.length} autre{concurrent.length > 1 ? "s" : ""} absence{concurrent.length > 1 ? "s" : ""} cette période
                </span>
                <span style={{ fontSize: 11, color: "#aaa" }}>
                  ({concurrent.slice(0, 2).map(c => c.prenom).join(", ")}{concurrent.length > 2 ? "…" : ""})
                </span>
              </div>
            )}

            {/* Indicateur multi-responsables : combien ont déjà validé + qui reste */}
            {l.statut === "pending" && Array.isArray(l.approvals) && l.approvals.filter(a => a.associe_id).length > 1 && (
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {(() => {
                  const approvs = l.approvals.filter(a => a.associe_id);
                  const nbOk = approvs.filter(a => a.statut === "approved").length;
                  const total = approvs.length;
                  const enAttente = approvs
                    .filter(a => a.statut === "pending")
                    .map(a => a.prenom ? `${a.prenom} ${a.nom || ""}`.trim() : "responsable");
                  return (
                    <>
                      <span style={{
                        fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 600,
                        background: nbOk === total ? "#EAF3DE" : "#f0f0f0",
                        color: nbOk === total ? "#3B6D11" : "#888",
                      }}>
                        👥 {nbOk}/{total} responsable{total > 1 ? "s" : ""} {nbOk === total ? "ont validé ✅" : "ont validé"}
                      </span>
                      {enAttente.length > 0 && (
                        <span style={{ fontSize: 10, color: "#854F0B", background: "#FAEEDA", padding: "2px 8px", borderRadius: 10, fontWeight: 600 }}>
                          ⏳ En attente de : {enAttente.join(", ")}
                        </span>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 8, flexShrink: 0, alignItems: "center" }}>
            {showActions && !isCancelRequest && (
              isProc ? (
                <span style={{
                  background: isProc === "approved" ? "#EAF3DE" : "#FCEBEB",
                  color: isProc === "approved" ? "#3B6D11" : "#A32D2D",
                  padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600
                }}>
                  {isProc === "approved" ? "✅ Approuvé..." : "❌ Refusé..."}
                </span>
              ) : (
                <>
                  <button onClick={() => handleStatut(l.id, "approved", l.nom, l.prenom)}
                    style={{ background: "#EAF3DE", color: "#3B6D11", border: "1px solid #97C459", borderRadius: 8, padding: "7px 16px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                    ✓ Approuver
                  </button>
                  <button onClick={() => handleStatut(l.id, "refused", l.nom, l.prenom)}
                    style={{ background: "#FCEBEB", color: "#A32D2D", border: "1px solid #F09595", borderRadius: 8, padding: "7px 16px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                    ✕ Refuser
                  </button>
                </>
              )
            )}
            {isCancelRequest && (
              <>
                <button onClick={() => handleConfirmCancel(l.id, true, l.nom, l.prenom)}
                  style={{ background: "#EAF3DE", color: "#3B6D11", border: "1px solid #97C459", borderRadius: 8, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                  ✓ Confirmer
                </button>
                <button onClick={() => handleConfirmCancel(l.id, false, l.nom, l.prenom)}
                  style={{ background: "#FCEBEB", color: "#A32D2D", border: "1px solid #F09595", borderRadius: 8, padding: "7px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
                  ✕ Refuser
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const Section = ({ title, count, color, bg, children, empty }) => (
    <div style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{title}</span>
        {count > 0 && (
          <span style={{ background: bg, color, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>{count}</span>
        )}
      </div>
      {count === 0
        ? <div style={{ background: "#fafafa", border: "1px dashed #e0e0e0", borderRadius: 10, padding: "20px", fontSize: 13, color: "#bbb", textAlign: "center" }}>{empty}</div>
        : children
      }
    </div>
  );

  return (
    <div style={{ maxWidth: 1000 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 10 }}>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>✅ Validations</h2>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher..."
          style={{ padding: "7px 12px", border: "1px solid #e0e0e0", borderRadius: 8, fontSize: 13, width: 200, outline: "none" }} />
      </div>

      {notification && (
        <div style={{
          background: notifType === "success" ? "#EAF3DE" : notifType === "refused" ? "#FCEBEB" : "#FAEEDA",
          color: notifType === "success" ? "#3B6D11" : notifType === "refused" ? "#A32D2D" : "#854F0B",
          border: `1px solid ${notifType === "success" ? "#97C459" : notifType === "refused" ? "#F09595" : "#F5C97A"}`,
          padding: "12px 18px", borderRadius: 10, marginBottom: 20, fontSize: 14, fontWeight: 700
        }}>
          {notification}
        </div>
      )}

      <Section title="⚠️ Demandes d'annulation" count={cancelRequests.length}
        color="#854F0B" bg="#FAEEDA" empty="Aucune demande d'annulation.">
        {cancelRequests.map(l => <LeaveCard key={l.id} l={l} showActions={true} isCancelRequest={true} />)}
      </Section>

      <Section title="⏳ En attente de validation" count={pending.length}
        color="#185FA5" bg="#E6F1FB" empty="Aucune demande en attente. 🎉">
        {pending.map(l => <LeaveCard key={l.id} l={l} showActions={true} isCancelRequest={false} />)}
      </Section>

      {waitingOthers.length > 0 && (
        <Section title="✅ Validées par vous — en attente d'un autre responsable" count={waitingOthers.length}
          color="#3B6D11" bg="#EAF3DE" empty="">
          {waitingOthers.map(l => <LeaveCard key={l.id} l={l} showActions={false} isCancelRequest={false} />)}
        </Section>
      )}

      <Section title="📋 Traitées récemment" count={processed.length}
        color="#888" bg="#f0f0f0" empty="Aucune demande traitée.">
        {processed.slice(0, 20).map(l => <LeaveCard key={l.id} l={l} showActions={false} isCancelRequest={false} />)}
      </Section>
    </div>
  );
}

export default Validations;
